import type { Document } from "../document/Document";
import type { GroupLayer, Layer, RasterLayer } from "../document/Layer";
import { compose, rotate as affineRotate, transformBuffer, translate as affineTranslate } from "../pixel/affineTransform";
import { contentAwareFill } from "../pixel/contentAwareFill";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { Selection } from "../selection/Selection";
import type { Command } from "./Command";

// Change the document's canvas bounds, shifting every layer by a fixed
// (translateX, translateY) offset. Two modes:
//
//   - Non-destructive (clipPixels = false, the default): every layer keeps its
//     full pixel buffer; only its (x, y) offset shifts. Pixels outside the new
//     canvas live in memory but are off-canvas — mirrors Photoshop's
//     "Delete Cropped Pixels" off.
//
//   - Destructive (clipPixels = true): raster layers are clipped to the new
//     canvas. Layers fully outside are removed. Layers partially outside get a
//     newly-allocated PixelBuffer sized to the in-bounds region; the layer's
//     (x, y) is adjusted to point at that clipped region. Original buffers are
//     captured into a snapshot inside `apply` so invert can restore them.
//
// Translation convention: translate is the offset added to every layer's
// (x, y). For a typical crop where the user drags a rect at (rx, ry, rw, rh),
// the caller passes translateX = -rx, translateY = -ry, newWidth = rw,
// newHeight = rh.

interface ClipSnapshot {
  // The doc.layers tree exactly as it was before apply ran. Restoring this
  // brings back every original pixel buffer and offset, including layers we
  // removed because they fell off-canvas.
  readonly originalLayers: readonly Layer[];
  readonly originalWidth: number;
  readonly originalHeight: number;
}

export class CropCommand implements Command {
  readonly kind = "crop";
  private snapshot: ClipSnapshot | null = null;

  /**
   * Populated by `apply()` when `contentAware` is true and the crop rect
   * extends beyond the original canvas on one or more sides — the list of
   * newly-exposed (previously off-canvas, now-transparent) margin rects in
   * the *new* document's coordinate space (#i105).
   *
   * `contentAwareFill` (`../pixel/contentAwareFill`) is a DOM-free core
   * module, so `apply()` runs it directly on every raster layer that has
   * nearby content to sample from — see `fillContentAwareMargins` below.
   * This field stays exposed mainly for introspection/testing (it also
   * doubles as a cheap "did the fill actually get considered" signal for
   * whatever UI wants to report on the crop).
   *
   * Null when content-aware fill doesn't apply: `contentAware` is false,
   * the crop didn't expand the canvas, or this is a rotated/straightened
   * crop (which resamples the whole canvas — "margin" isn't a simple
   * additive rect there, see the rotation branch below).
   */
  contentAwareRegions: Rect[] | null = null;

  constructor(
    private readonly newWidth: number,
    private readonly newHeight: number,
    private readonly translateX: number,
    private readonly translateY: number,
    private readonly clipPixels: boolean = false,
    // Rotation (radians) of the crop rect about its own center — set when
    // the user drags the Crop tool's rotation handle or uses Straighten
    // (#i21). Committing a rotated rect must "straighten" the image: rotate
    // the canvas by -rotation about the rect's center, then crop to the
    // now-upright rect. Zero (the default) is a plain axis-aligned crop and
    // behaves exactly as before.
    private readonly rotation: number = 0,
    // When true and the committed crop rect extends past the original
    // canvas bounds, the newly-exposed margin is filled via content-aware
    // fill instead of staying transparent (#i105 — mirrors Photoshop's
    // Crop tool "Content-Aware" checkbox). See `contentAwareRegions` above
    // and `fillContentAwareMargins` below for how the fill is applied.
    private readonly contentAware: boolean = false,
  ) {}

  apply(doc: Document): Document {
    const cropRect = {
      x: -this.translateX,
      y: -this.translateY,
      w: this.newWidth,
      h: this.newHeight,
    };

    // Compute newly-exposed margin regions for content-aware fill (#i105).
    // Only meaningful for the plain (non-rotated) crop path below — see the
    // `contentAwareRegions` doc comment for why rotation is excluded. An
    // empty result (crop didn't actually expand past the original bounds)
    // normalizes to null, same as "not applicable".
    const expanded = (this.contentAware && this.rotation === 0)
      ? computeExpandedRegions(this.translateX, this.translateY, doc.width, doc.height, this.newWidth, this.newHeight)
      : [];
    this.contentAwareRegions = expanded.length > 0 ? expanded : null;

    if (this.rotation !== 0) {
      // Straighten: resampling is inherently destructive (a rotated sample
      // can't be expressed as a plain (x, y) offset into the existing
      // buffer), so this path ignores `clipPixels` and always bakes new
      // pixel buffers, same as the destructive branch below.
      this.snapshot = {
        originalLayers: doc.layers,
        originalWidth: doc.width,
        originalHeight: doc.height,
      };
      const centerX = cropRect.x + cropRect.w / 2;
      const centerY = cropRect.y + cropRect.h / 2;
      const layers = rotateCropLayers(
        doc.layers,
        centerX,
        centerY,
        this.rotation,
        this.newWidth,
        this.newHeight,
      );
      const selection = rotateSelection(
        doc.selection,
        centerX,
        centerY,
        this.rotation,
        this.newWidth,
        this.newHeight,
      );
      return {
        ...doc,
        width: this.newWidth,
        height: this.newHeight,
        layers,
        selection,
      };
    }

    // Crop the selection mask to the new doc bounds (#69, #81).
    const selection = cropSelection(doc.selection, cropRect);

    if (this.clipPixels) {
      this.snapshot = {
        originalLayers: doc.layers,
        originalWidth: doc.width,
        originalHeight: doc.height,
      };
      const layers = clipLayers(
        doc.layers,
        this.translateX,
        this.translateY,
        this.newWidth,
        this.newHeight,
      );
      // Also crop layer masks to the new bounds (#70).
      const layersWithMasks = cropLayerMasks(layers, cropRect);
      const finalLayers = expanded.length > 0
        ? fillContentAwareMargins(layersWithMasks, expanded, this.newWidth, this.newHeight)
        : layersWithMasks;
      return {
        ...doc,
        width: this.newWidth,
        height: this.newHeight,
        layers: finalLayers,
        selection,
      };
    }

    const layers = translateLayers(doc.layers, this.translateX, this.translateY);
    const layersWithMasks = translateLayerMasks(layers, cropRect);
    if (expanded.length > 0) {
      // Content-aware fill on the non-destructive path still mutates raster
      // pixel buffers (grows them to the new canvas and bakes in sampled
      // content), so — even though `clipPixels` is false here — this needs
      // the same snapshot/RestoreCropCommand undo path the destructive
      // branch uses, instead of the plain re-translate inverse below (#i105).
      this.snapshot = {
        originalLayers: doc.layers,
        originalWidth: doc.width,
        originalHeight: doc.height,
      };
      const finalLayers = fillContentAwareMargins(layersWithMasks, expanded, this.newWidth, this.newHeight);
      return {
        ...doc,
        width: this.newWidth,
        height: this.newHeight,
        layers: finalLayers,
        selection,
      };
    }
    return {
      ...doc,
      width: this.newWidth,
      height: this.newHeight,
      layers: layersWithMasks,
      selection,
    };
  }

  invert(before: Document, _after: Document): Command {
    if (this.rotation !== 0 || this.clipPixels || this.snapshot) {
      const snap = this.snapshot;
      return new RestoreCropCommand(
        snap ?? {
          originalLayers: before.layers,
          originalWidth: before.width,
          originalHeight: before.height,
        },
        this.newWidth,
        this.newHeight,
        this.translateX,
        this.translateY,
        this.clipPixels,
        this.rotation,
        this.contentAware,
      );
    }
    // Non-destructive with no content-aware fill applied: a plain inverse
    // CropCommand suffices — the layer buffers were never touched.
    return new CropCommand(
      before.width,
      before.height,
      -this.translateX,
      -this.translateY,
      false,
    );
  }
}

// Inverse of a destructive (or rotated) crop. Restores the original layer
// tree (and thus the original pixel buffers, exactly — no lossy re-sample)
// and original document dimensions.
class RestoreCropCommand implements Command {
  readonly kind = "crop/restore";
  constructor(
    private readonly snapshot: ClipSnapshot,
    private readonly croppedWidth: number,
    private readonly croppedHeight: number,
    private readonly translateX: number,
    private readonly translateY: number,
    private readonly clipPixels: boolean,
    private readonly rotation: number = 0,
    // Threaded through so redo (invert of this invert) re-applies
    // content-aware fill instead of silently downgrading to a plain crop
    // (#i105).
    private readonly contentAware: boolean = false,
  ) {}

  apply(doc: Document): Document {
    return {
      ...doc,
      width: this.snapshot.originalWidth,
      height: this.snapshot.originalHeight,
      layers: this.snapshot.originalLayers,
    };
  }

  invert(_before: Document, _after: Document): Command {
    return new CropCommand(
      this.croppedWidth,
      this.croppedHeight,
      this.translateX,
      this.translateY,
      this.clipPixels,
      this.rotation,
      this.contentAware,
    );
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Rotated crop ("straighten") — #i21
//
// The crop rect is tilted by `rotation` radians about its own center in the
// *original* document's coordinate space. To commit it, every raster layer
// (and its mask, and the selection) is resampled: for each destination pixel
// in the new (upright) canvas, we walk backwards through the rotation to find
// where that pixel came from in the original, tilted image. This is
// equivalent to "rotate the whole canvas by -rotation about the rect's
// center, then crop to the rect's upright bounds" in a single pass, and
// avoids ever materialising a full rotated copy of the document.
//
// Layer/mask resampling reuses the shared bilinear affine-warp helper
// (`transformBuffer`, from ../pixel/affineTransform — the same one Free
// Transform uses); source pixels outside the layer's original bounds
// contribute transparent/zero, so rotated corners expose empty pixels
// exactly like Photoshop's straighten (content-aware fill, if enabled, is a
// separate pass layered on top elsewhere). The selection mask is single-
// channel, so it's resampled with its own small nearest-neighbor loop below.
// ---------------------------------------------------------------------------

/**
 * Resample a single pixel buffer through the rotation, about (centerX,
 * centerY), into a fresh newW×newH buffer — reusing the shared affine-warp
 * helper (`transformBuffer`, the same one Free Transform uses) for bilinear
 * sampling and premultiplied-alpha edge blending instead of a hand-rolled
 * nearest-neighbor loop. `originX/Y` is the buffer's own top-left corner in
 * the same doc-space coordinates that `centerX/Y` is expressed in.
 *
 * The forward matrix maps source-local (u, v) → destination-local (dx, dy):
 * shift into doc-space-relative-to-center coordinates, rotate by -rotation
 * (straighten), then shift into the destination canvas's local coordinates
 * (its center sits at (newW/2, newH/2)). `transformBuffer` inverse-maps this
 * for sampling.
 */
function resampleRotated(
  src: PixelBuffer,
  originX: number,
  originY: number,
  centerX: number,
  centerY: number,
  rotation: number,
  newW: number,
  newH: number,
): PixelBuffer {
  const matrix = compose(
    affineTranslate(newW / 2, newH / 2),
    compose(affineRotate(-rotation), affineTranslate(originX - centerX, originY - centerY)),
  );
  return transformBuffer(src, matrix, {
    outputWidth: newW,
    outputHeight: newH,
    sampling: "bilinear",
  });
}

/** Rotate-resample a raster layer (and its mask, if any) into a new
 * newW×newH buffer positioned at (0, 0) — the whole new canvas. */
function rotateCropRaster(
  layer: RasterLayer,
  centerX: number,
  centerY: number,
  rotation: number,
  newW: number,
  newH: number,
): RasterLayer {
  const pixels = resampleRotated(
    layer.pixels, layer.x, layer.y, centerX, centerY, rotation, newW, newH,
  );
  if (!layer.mask) {
    // No mask key at all — avoids assigning an explicit `undefined` to an
    // optional property under `exactOptionalPropertyTypes`.
    return { ...layer, x: 0, y: 0, pixels };
  }
  // mask.x/y are relative to the (pre-crop) layer origin.
  const maskPixels = resampleRotated(
    layer.mask.pixels,
    layer.x + layer.mask.x,
    layer.y + layer.mask.y,
    centerX,
    centerY,
    rotation,
    newW,
    newH,
  );
  return {
    ...layer,
    x: 0,
    y: 0,
    pixels,
    mask: { ...layer.mask, x: 0, y: 0, pixels: maskPixels },
  };
}

// Recurse through the layer tree, rotate-resampling every raster. Mirrors
// clipLayers' choice to drop non-raster/non-group layers (adjustment, type,
// shape, …) in a destructive path — rotation is always destructive.
function rotateCropLayers(
  layers: readonly Layer[],
  centerX: number,
  centerY: number,
  rotation: number,
  newW: number,
  newH: number,
): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.kind === "group") {
      const group: GroupLayer = {
        ...l,
        x: 0,
        y: 0,
        children: rotateCropLayers(l.children, centerX, centerY, rotation, newW, newH),
      };
      out.push(group);
      continue;
    }
    if (l.kind !== "raster") continue;
    out.push(rotateCropRaster(l, centerX, centerY, rotation, newW, newH));
  }
  return out;
}

/** Rotate-resample the selection mask the same way as layer pixels. */
function rotateSelection(
  sel: Selection | null,
  centerX: number,
  centerY: number,
  rotation: number,
  newW: number,
  newH: number,
): Selection | null {
  if (!sel) return null;
  const { mask: src, docW: srcW, docH: srcH } = sel;
  const dst = new Uint8Array(newW * newH);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  for (let dy = 0; dy < newH; dy++) {
    // Same integer (no half-pixel offset) convention as `resampleRotated`'s
    // affine matrix, so the selection mask stays aligned with the resampled
    // layer pixels.
    const ly = dy - newH / 2;
    for (let dx = 0; dx < newW; dx++) {
      const lx = dx - newW / 2;
      const docX = centerX + lx * cos - ly * sin;
      const docY = centerY + lx * sin + ly * cos;
      const sx = Math.floor(docX);
      const sy = Math.floor(docY);
      if (sx < 0 || sx >= srcW || sy < 0 || sy >= srcH) continue;
      dst[dy * newW + dx] = src[sy * srcW + sx] ?? 0;
    }
  }
  return Selection.fromMask(newW, newH, dst);
}

// Non-destructive: structurally share pixels, only offsets change.
function translateLayers(
  layers: readonly Layer[],
  dx: number,
  dy: number,
): Layer[] {
  return layers.map((l) => translateLayer(l, dx, dy));
}

function translateLayer(layer: Layer, dx: number, dy: number): Layer {
  if (layer.kind === "group") {
    const group: GroupLayer = {
      ...layer,
      x: layer.x + dx,
      y: layer.y + dy,
      children: translateLayers(layer.children, dx, dy),
    };
    return group;
  }
  return { ...layer, x: layer.x + dx, y: layer.y + dy };
}

// Destructive: shift each layer and, for rasters, clip the pixel buffer to
// the new doc bounds. Off-canvas rasters drop out entirely. Groups recurse;
// an empty group is preserved (Photoshop keeps empty groups around).
function clipLayers(
  layers: readonly Layer[],
  dx: number,
  dy: number,
  newW: number,
  newH: number,
): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.kind === "group") {
      const group: GroupLayer = {
        ...l,
        x: l.x + dx,
        y: l.y + dy,
        children: clipLayers(l.children, dx, dy, newW, newH),
      };
      out.push(group);
      continue;
    }
    if (l.kind !== "raster") continue;
    const clipped = clipRaster(l, dx, dy, newW, newH);
    if (clipped) out.push(clipped);
  }
  return out;
}

// Clip a single raster layer to the new doc bounds. Returns null if the layer
// is fully outside. Otherwise returns a new RasterLayer whose pixel buffer is
// sized to the in-bounds region (so we don't carry around off-canvas pixels)
// and whose (x, y) points at that region.
function clipRaster(
  layer: RasterLayer,
  dx: number,
  dy: number,
  newW: number,
  newH: number,
): RasterLayer | null {
  // Position the layer is "moving to" in the new doc's coordinate system.
  const lx = layer.x + dx;
  const ly = layer.y + dy;
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;

  // Intersect the layer's footprint [lx, lx+lw) x [ly, ly+lh) with the new
  // canvas [0, newW) x [0, newH).
  const x0 = Math.max(0, lx);
  const y0 = Math.max(0, ly);
  const x1 = Math.min(newW, lx + lw);
  const y1 = Math.min(newH, ly + lh);
  const w = x1 - x0;
  const h = y1 - y0;
  if (w <= 0 || h <= 0) return null;

  // If the layer is fully inside the new canvas and starts at an integer
  // offset, we can keep the buffer as-is — no new allocation needed.
  if (lx >= 0 && ly >= 0 && lx + lw <= newW && ly + lh <= newH) {
    return { ...layer, x: lx, y: ly };
  }

  // Otherwise allocate a smaller buffer and copy the in-bounds region.
  const next = new PixelBuffer(w, h, layer.pixels.mode);
  const srcOffsetX = x0 - lx; // column in the original buffer that maps to col 0 in `next`
  const srcOffsetY = y0 - ly;
  const src = layer.pixels.data;
  const dst = next.data;
  for (let row = 0; row < h; row++) {
    const srcRow = (srcOffsetY + row) * lw + srcOffsetX;
    const dstRow = row * w;
    const srcStart = srcRow * 4;
    const dstStart = dstRow * 4;
    // Row-wise copy: 4 bytes per pixel, w pixels.
    dst.set(src.subarray(srcStart, srcStart + w * 4), dstStart);
  }
  next.markDirty();
  return { ...layer, x: x0, y: y0, pixels: next };
}

// ---------------------------------------------------------------------------
// Selection & mask cropping helpers (#69, #70, #81)
// ---------------------------------------------------------------------------

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Content-aware fill region computation (#i105)
// ---------------------------------------------------------------------------

/**
 * Compute the newly-exposed margin rects when a crop expands the canvas
 * beyond the original document's bounds. Returns rects in the *new*
 * document's coordinate space, tiled so they never overlap (top/bottom
 * strips span the full new width, including corners; left/right strips
 * only cover the vertical band that top/bottom didn't already claim).
 *
 * Returns an empty array when the crop doesn't expand past the original
 * bounds on any side (a plain inward/equal crop has no margin to fill).
 */
function computeExpandedRegions(
  translateX: number,
  translateY: number,
  origW: number,
  origH: number,
  newW: number,
  newH: number,
): Rect[] {
  // The original canvas's footprint, expressed in the new canvas's
  // coordinate space (every layer is shifted by (translateX, translateY)).
  const origX0 = translateX;
  const origY0 = translateY;
  const origX1 = translateX + origW;
  const origY1 = translateY + origH;

  const regions: Rect[] = [];

  // Top margin: full new width, rows above the original footprint.
  if (origY0 > 0) {
    regions.push({ x: 0, y: 0, w: newW, h: Math.min(origY0, newH) });
  }
  // Bottom margin: full new width, rows below the original footprint.
  if (origY1 < newH) {
    const y = Math.max(origY1, 0);
    regions.push({ x: 0, y, w: newW, h: newH - y });
  }

  // Left/right margins only need the vertical band the top/bottom strips
  // didn't already cover, so corners aren't double-counted.
  const midY0 = Math.max(0, origY0);
  const midY1 = Math.min(newH, origY1);
  if (midY1 > midY0) {
    if (origX0 > 0) {
      regions.push({ x: 0, y: midY0, w: Math.min(origX0, newW), h: midY1 - midY0 });
    }
    if (origX1 < newW) {
      const x = Math.max(origX1, 0);
      regions.push({ x, y: midY0, w: newW - x, h: midY1 - midY0 });
    }
  }

  return regions;
}

/**
 * Fill newly-exposed margin `regions` (in the new document's coordinate
 * space) on every raster layer that has nearby content to sample from,
 * using content-aware sampling (#i105 — mirrors Photoshop's Crop tool
 * "Content-Aware" checkbox). Recurses into groups; every other layer kind
 * (adjustment, type, shape, fill, background, …) passes through untouched.
 *
 * Runs after the layer tree has already been translated/clipped into the
 * new document's coordinate space, so `layer.x/y` are already final.
 */
function fillContentAwareMargins(
  layers: readonly Layer[],
  regions: readonly Rect[],
  docW: number,
  docH: number,
): Layer[] {
  return layers.map((l) => {
    if (l.kind === "group") {
      return { ...l, children: fillContentAwareMargins(l.children, regions, docW, docH) };
    }
    if (l.kind !== "raster") return l;
    return fillRasterMargins(l, regions, docW, docH);
  });
}

/**
 * Content-aware-fill a single raster layer's margin pixels. Embeds the
 * layer's current pixels into a doc-sized (`docW`x`docH`) buffer first —
 * `contentAwareFill`'s mask is indexed by its source buffer's own width, so
 * the mask and the buffer it fills must share one coordinate space, and the
 * margin `regions` are already expressed in doc space. When the fill
 * actually runs, the layer becomes canvas-sized at (0, 0), matching
 * Photoshop's content-aware crop (the layer now covers the whole enlarged
 * canvas instead of leaving the margin an empty gap).
 *
 * Returns the layer unchanged (identical reference) when there's nothing to
 * fill for it: no margin pixel is transparent on this layer (it either
 * doesn't reach the margin at all, or already has real off-canvas content
 * there that shouldn't be overwritten), or the layer has no non-transparent
 * content anywhere to sample from (a fully-empty layer is left untouched).
 */
function fillRasterMargins(
  layer: RasterLayer,
  regions: readonly Rect[],
  docW: number,
  docH: number,
): RasterLayer {
  const canvas = embedInCanvas(layer, docW, docH);
  const mask = buildMarginMask(canvas, regions);
  if (!mask) return layer;

  const filled = contentAwareFill(canvas, mask, {
    samplingRadius: Math.max(docW, docH),
  });
  return { ...layer, x: 0, y: 0, pixels: filled };
}

/** Copy a raster layer's pixels into a fresh doc-sized buffer at its
 * current (x, y), clipped to the doc bounds. Out-of-bounds/uncovered area
 * stays transparent (fresh `PixelBuffer`s are zero-initialized). */
function embedInCanvas(layer: RasterLayer, docW: number, docH: number): PixelBuffer {
  const canvas = new PixelBuffer(docW, docH, layer.pixels.mode);
  const src = layer.pixels;
  const lw = src.width;
  const lh = src.height;
  const x0 = Math.max(0, layer.x);
  const y0 = Math.max(0, layer.y);
  const x1 = Math.min(docW, layer.x + lw);
  const y1 = Math.min(docH, layer.y + lh);
  if (x1 <= x0 || y1 <= y0) return canvas; // layer doesn't overlap the new canvas at all

  const srcOffsetX = x0 - layer.x;
  const srcOffsetY = y0 - layer.y;
  const w = x1 - x0;
  const rows = y1 - y0;
  const srcData = src.data;
  const dstData = canvas.data;
  for (let row = 0; row < rows; row++) {
    const srcRowStart = ((srcOffsetY + row) * lw + srcOffsetX) * 4;
    const dstRowStart = ((y0 + row) * docW + x0) * 4;
    dstData.set(srcData.subarray(srcRowStart, srcRowStart + w * 4), dstRowStart);
  }
  return canvas;
}

/**
 * Build a `contentAwareFill` mask (sized to `canvas`, i.e. docW x docH)
 * marking pixels that fall inside one of the margin `regions` AND are
 * currently transparent on `canvas`. Returns null when there's nothing to
 * fill (no margin pixel is transparent) or nothing to fill *from* (the
 * layer has no non-transparent content outside the mask at all).
 */
function buildMarginMask(canvas: PixelBuffer, regions: readonly Rect[]): Uint8Array | null {
  const w = canvas.width;
  const h = canvas.height;
  const data = canvas.data;
  const mask = new Uint8Array(w * h);
  let hasMarginPixel = false;

  for (const r of regions) {
    const x0 = Math.max(0, Math.floor(r.x));
    const y0 = Math.max(0, Math.floor(r.y));
    const x1 = Math.min(w, Math.ceil(r.x + r.w));
    const y1 = Math.min(h, Math.ceil(r.y + r.h));
    for (let y = y0; y < y1; y++) {
      const rowBase = y * w;
      for (let x = x0; x < x1; x++) {
        const idx = rowBase + x;
        if (data[idx * 4 + 3] === 0) {
          mask[idx] = 255;
          hasMarginPixel = true;
        }
      }
    }
  }
  if (!hasMarginPixel) return null;

  // Leave fully-empty layers untouched — with no non-transparent pixel
  // outside the mask, there's nothing for contentAwareFill to sample; it
  // would just copy transparent-into-transparent.
  let hasContent = false;
  for (let i = 0; i < w * h; i++) {
    if (mask[i] === 0 && data[i * 4 + 3] !== 0) {
      hasContent = true;
      break;
    }
  }
  if (!hasContent) return null;

  return mask;
}

/** Crop a selection mask to the new doc bounds. */
function cropSelection(sel: Selection | null, crop: Rect): Selection | null {
  if (!sel) return null;
  const { mask: src, docW: srcW, docH: srcH } = sel;
  // Intersect crop rect with source doc bounds to get the region we keep.
  const x0 = Math.max(0, crop.x);
  const y0 = Math.max(0, crop.y);
  const x1 = Math.min(srcW, crop.x + crop.w);
  const y1 = Math.min(srcH, crop.y + crop.h);
  const w = x1 - x0;
  const h = y1 - y0;
  if (w <= 0 || h <= 0) return Selection.empty(crop.w, crop.h);

  const dst = new Uint8Array(crop.w * crop.h);
  for (let row = 0; row < h; row++) {
    const srcOff = (y0 + row) * srcW + x0;
    const dstOff = row * crop.w;
    dst.set(src.subarray(srcOff, srcOff + w), dstOff);
  }
  return Selection.fromMask(crop.w, crop.h, dst);
}

/** Recursively crop layer masks to the new doc bounds. */
function cropLayerMasks(
  layers: readonly Layer[],
  crop: Rect,
): Layer[] {
  return layers.map((l) => {
    if (l.kind === "group") {
      return { ...l, children: cropLayerMasks(l.children, crop) };
    }
    if (l.kind !== "raster" || !l.mask) return l;
    const cropped = cropMaskPixels(l.mask, crop);
    return cropped ? { ...l, mask: cropped } : l;
  });
}

/** Translate-only: layer masks shift position but keep their pixel buffer. */
function translateLayerMasks(
  layers: readonly Layer[],
  crop: Rect,
): Layer[] {
  return layers.map((l) => {
    if (l.kind === "group") {
      return { ...l, children: translateLayerMasks(l.children, crop) };
    }
    if (l.kind !== "raster" || !l.mask) return l;
    const mx = l.mask.x + crop.x;
    const my = l.mask.y + crop.y;
    return { ...l, mask: { ...l.mask, x: mx, y: my } };
  });
}

/** Crop a layer mask's pixel buffer to the new doc bounds. Returns null if fully outside. */
function cropMaskPixels(
  mask: NonNullable<RasterLayer["mask"]>,
  crop: Rect,
): NonNullable<RasterLayer["mask"]> | null {
  const { pixels: buf, x: mx, y: my, ...rest } = mask;
  const srcW = buf.width;
  const srcH = buf.height;
  const lx = mx + crop.x;
  const ly = my + crop.y;
  const x0 = Math.max(0, lx);
  const y0 = Math.max(0, ly);
  const x1 = Math.min(crop.w, mx + srcW + crop.x);
  const y1 = Math.min(crop.h, my + srcH + crop.y);
  const w = x1 - x0;
  const h = y1 - y0;
  if (w <= 0 || h <= 0) return null;

  if (lx >= 0 && ly >= 0 && lx + srcW <= crop.w && ly + srcH <= crop.h) {
    return { ...rest, x: lx, y: ly, pixels: buf };
  }

  const srcOffsetX = x0 - lx;
  const srcOffsetY = y0 - ly;
  const src = buf.data;
  const next = new PixelBuffer(w, h, buf.mode);
  const dst = next.data;
  for (let row = 0; row < h; row++) {
    const srcRow = (srcOffsetY + row) * srcW + srcOffsetX;
    const dstRow = row * w;
    dst.set(src.subarray(srcRow * 4, srcRow * 4 + w * 4), dstRow * 4);
  }
  next.markDirty();
  return { ...rest, x: x0, y: y0, pixels: next };
}
